<script setup lang="ts">
import BannerArrowIcon from '~/assets/images/home/wiki-store-banner-arrow.svg'
import type { TradingAppTraderAliasMap } from '~/utils/homeTradingApps'
import { resolveStoreBannerLink, type StoreBannerItem } from '~/utils/homeStoreBanner'

defineOptions({
  name: 'HomeStoreAdBanner',
})

const props = withDefaults(
  defineProps<{
    /** 首页广告条目；保留单条入参兼容旧页面调用。 */
    banner?: StoreBannerItem | null
    /** 首页广告条目列表；有值时优先渲染列表，超过三条时支持左右滑动。 */
    banners?: readonly StoreBannerItem[] | null
    /** 交易商编码到品牌别名的映射，用于点击广告后跳转服务商详情页。 */
    traderAliases?: Readonly<TradingAppTraderAliasMap> | null
  }>(),
  {
    banner: null,
    banners: () => [],
    traderAliases: null,
  },
)

const { locale } = useAppI18n()
const { isRtl } = useRtl()

/** 鼠标拖拽判定阈值，低于该位移视为普通点击。 */
const DRAG_START_THRESHOLD = 8

/** 拖拽结束后短暂抑制点击，避免滑动广告位时误触卡片上的外链。 */
const DRAG_CLICK_SUPPRESS_DURATION = 250

const trackRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)
let dragState: { pointerId: number; lastX: number; startX: number } | null = null
let suppressClickUntil = 0

const nuxtLinkComponent = resolveComponent('NuxtLink')

/** 将新旧两种入参归一为广告数组，并忽略没有背景图的无效条目。 */
const displayBanners = computed(() => {
  const source = props.banners?.length ? props.banners : props.banner ? [props.banner] : []

  return source.filter((item) => Boolean(item?.bannerImage?.trim()))
})

/** 广告位一屏展示三张；数据不足三张时均分上级容器宽度。 */
const cardBasis = computed(() => {
  const total = displayBanners.value.length

  if (total <= 1) return '100%'
  if (total === 2) return 'calc((100% - 20px) / 2)'

  return 'calc((100% - 40px) / 3)'
})

/** 只有超过一屏的广告才显示悬浮切换按钮。 */
const hasNavigationControls = computed(() => displayBanners.value.length > 3)

/** 返回广告标题，软件广告优先显示应用名，服务商广告回退到交易商名称。 */
const getBannerTitle = (banner: StoreBannerItem) => {
  return banner.appName?.trim() || banner.traderName?.trim() || banner.jumpTypeName?.trim() || '广告'
}

/**
 * 返回广告副标题，只使用接口的应用描述。
 * `ad` 字段在当前数据里固定是角标文案 `AD`，回退到它会让卡片重复出现 `AD`。
 */
const getBannerDescription = (banner: StoreBannerItem) => {
  return banner.appTitle?.trim() || ''
}

/** 构造广告点击目标：交易应用跳详情页、服务商跳交易所详情页、其余走安全外链。 */
const getBannerHref = (banner: StoreBannerItem) => {
  return resolveStoreBannerLink(banner, { languageCode: locale, traderAliases: props.traderAliases })
}

/** 仅在广告存在可跳转地址时挂载 NuxtLink 外链属性，普通卡片保持语义干净。 */
const getBannerLinkProps = (banner: StoreBannerItem) => {
  const href = getBannerHref(banner)
  if (!href) return {}

  return {
    to: href,
    external: true,
    target: '_blank',
    rel: 'noopener noreferrer',
  }
}

/** 记录鼠标拖拽起点；触屏保留浏览器原生横向滚动与吸附。 */
const startTrackDrag = (event: PointerEvent) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) return

  dragState = { pointerId: event.pointerId, lastX: event.clientX, startX: event.clientX }
}

/** 位移超过阈值后才接管指针并滚动轨道，避免影响卡片自身点击。 */
const moveTrackDrag = (event: PointerEvent) => {
  const track = trackRef.value
  const state = dragState

  if (!track || !state || state.pointerId !== event.pointerId) return

  if (!isDragging.value) {
    if (Math.abs(event.clientX - state.startX) < DRAG_START_THRESHOLD) return

    isDragging.value = true
    track.setPointerCapture(event.pointerId)
  }

  const deltaX = event.clientX - state.lastX
  if (deltaX === 0) return

  track.scrollLeft -= deltaX * (isRtl.value ? -1 : 1)
  state.lastX = event.clientX
  suppressClickUntil = Date.now() + DRAG_CLICK_SUPPRESS_DURATION
  event.preventDefault()
}

/** 结束拖拽并释放指针捕获，恢复后续点击。 */
const endTrackDrag = (event: PointerEvent) => {
  const track = trackRef.value

  if (track?.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId)

  dragState = null
  isDragging.value = false
}

/** 指针被浏览器取消时清理拖拽态，避免下一次滑动误触发。 */
const cancelTrackDrag = (event: PointerEvent) => {
  if (dragState?.pointerId !== event.pointerId) return

  endTrackDrag(event)
}

/** 拖拽刚结束时吞掉卡片上的点击，避免滑动后意外打开广告链接。 */
const handleTrackClick = (event: MouseEvent) => {
  if (Date.now() > suppressClickUntil) return

  event.preventDefault()
  event.stopPropagation()
}

/** 按一张卡片的宽度加间距滚动，按钮与拖拽共用同一条横向轨道。 */
const scrollByCard = (direction: 'previous' | 'next') => {
  const track = trackRef.value
  const firstCard = track?.firstElementChild

  if (!track || !(firstCard instanceof HTMLElement)) return

  const styles = window.getComputedStyle(track)
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0
  const distance = firstCard.getBoundingClientRect().width + gap
  const directionSign = direction === 'next' ? 1 : -1

  track.scrollBy({
    left: distance * directionSign * (isRtl.value ? -1 : 1),
    behavior: 'smooth',
  })
}
</script>

<template>
  <div v-if="displayBanners.length" class="group relative h-[218px] w-full">
    <div
      ref="trackRef"
      class="no-scrollbar flex h-full w-full snap-x snap-mandatory gap-[20px] overflow-x-auto overflow-y-hidden overscroll-x-contain select-none"
      :class="isDragging ? 'cursor-grabbing' : ''"
      role="list"
      @pointerdown="startTrackDrag"
      @pointermove="moveTrackDrag"
      @pointerup="endTrackDrag"
      @pointercancel="cancelTrackDrag"
      @dragstart.prevent
      @click.capture="handleTrackClick"
    >
      <component
        :is="getBannerHref(banner) ? nuxtLinkComponent : 'article'"
        v-for="(banner, bannerIndex) in displayBanners"
        :key="banner.id || `${banner.bannerImage}-${bannerIndex}`"
        class="group relative h-[218px] min-w-0 shrink-0 snap-start overflow-hidden rounded-[12px] bg-[#FAFAFA] outline-none"
        :class="
          getBannerHref(banner)
            ? 'cursor-pointer hover:brightness-[1.04] focus-visible:ring-[2px] focus-visible:ring-[#112d73]'
            : ''
        "
        :style="{ flexBasis: cardBasis }"
        v-bind="getBannerLinkProps(banner)"
        :aria-label="getBannerTitle(banner)"
        role="listitem"
      >
        <CommonImage
          :src="banner.bannerImage"
          :alt="getBannerTitle(banner)"
          class="absolute inset-0 size-full transition-transform duration-300 ease-out group-hover:scale-[1.02]"
          height="218"
          loading="lazy"
          object-fit="cover"
          :fallback-src="false"
        />

        <span
          class="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[74px] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.6)_100%)]"
          aria-hidden="true"
        ></span>

        <span
          class="pointer-events-none absolute [inset-inline-end:16px] top-[16px] z-[2] flex h-[20px] items-center justify-center rounded-[999px] bg-[rgba(0,0,0,0.1)] px-[8px] text-[12px] leading-[20px] font-[600] text-white"
          aria-hidden="true"
        >
          AD
        </span>

        <span
          class="pointer-events-none absolute inset-x-[16px] bottom-[20px] z-[2] flex min-w-0 items-center gap-[10px] text-start text-white"
        >
          <CommonImage
            v-if="banner.appLogo?.trim() || banner.traderIco?.trim()"
            :src="banner.appLogo?.trim() || banner.traderIco?.trim()"
            :alt="`${getBannerTitle(banner)} 图标`"
            class="size-[32px] shrink-0 rounded-[6px] border border-[rgba(255,255,255,0.3)]"
            width="32"
            height="32"
            loading="lazy"
            object-fit="cover"
            :fallback-src="false"
          />

          <span class="flex min-w-0 flex-1 flex-col gap-[2px]">
            <strong class="truncate text-[14px] leading-[20px] font-[400] text-white">
              {{ getBannerTitle(banner) }}
            </strong>
            <span
              v-if="getBannerDescription(banner)"
              class="truncate text-[12px] leading-[18px] font-[400] text-white/60"
            >
              {{ getBannerDescription(banner) }}
            </span>
          </span>
        </span>
      </component>
    </div>

    <div
      v-if="hasNavigationControls"
      class="pointer-events-none absolute inset-0 z-[3] flex items-center justify-between px-[12px]"
    >
      <button
        type="button"
        class="pointer-events-none flex size-[32px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] text-white opacity-0 backdrop-blur-[10px] transition duration-[250ms] group-hover:pointer-events-auto group-hover:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:opacity-100 hover:bg-[rgba(5,12,31,0.62)]"
        aria-label="Previous banner"
        @click="scrollByCard('previous')"
      >
        <BannerArrowIcon class="size-[24px] rotate-180 rtl:rotate-0" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="pointer-events-none flex size-[32px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] text-white opacity-0 backdrop-blur-[10px] transition duration-[250ms] group-hover:pointer-events-auto group-hover:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:opacity-100 hover:bg-[rgba(5,12,31,0.62)]"
        aria-label="Next banner"
        @click="scrollByCard('next')"
      >
        <BannerArrowIcon class="size-[24px] rtl:rotate-180" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
